import { Check, Loader2, Circle, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { BiBuild } from "@/resources/brand-intelligence/brand-intelligence.api";

// AUT-245 section 1.1: the building card. Every tab shows this identical card while a
// build is in flight. Driven by REAL per-stage backend status (not a simulated timer).
export function BrandIntelligenceBuildingState({ build }: { build: BiBuild }) {
  const stages = build.stages ?? [];
  const done = stages.filter((s) => s.status === "complete" || s.status === "skipped").length;
  const pct = stages.length > 0 ? Math.round((done / stages.length) * 100) : 0;
  const failed = build.status === "failed";

  return (
    <Card>
      <CardContent className="space-y-6 py-10">
        <div className="space-y-1 text-center">
          <h3 className="text-base font-semibold text-foreground">Building Brand Intelligence</h3>
          <p className="text-sm text-muted-foreground">
            Collecting category, competitor, and keyword data — first report in a few minutes.
          </p>
        </div>

        <div className="mx-auto max-w-md space-y-4">
          <div className="space-y-1">
            <Progress value={pct} />
            <p className="text-right text-xs tabular-nums text-muted-foreground">{pct}% complete</p>
          </div>

          <ul className="space-y-2">
            {stages.map((s) => (
              <li key={s.key} className="flex items-center gap-2 text-sm">
                {s.status === "complete" ? (
                  <Check className="h-4 w-4 shrink-0 text-status-success-strong" />
                ) : s.status === "skipped" ? (
                  <Minus className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : s.status === "building" ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                )}
                <span className={cn(s.status === "building" ? "font-medium text-foreground" : "text-muted-foreground")}>
                  {s.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {failed && (
          <p className="text-center text-xs text-status-error-strong">
            Build failed{build.error ? `: ${build.error}` : "."}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
